import {
  AccountOwner,
  AccountOwnerKind,
  AccountType,
  ValidationResult,
} from '../../../services/credit-vetting.service';
import { ICacheStore } from '../context/cache-context';
import { request } from '../../../services/request';
import { parseObjectToQueryParamString } from 'src/helpers/functional-helpers';
import { Api } from 'src/common';
import { IdType } from '../types/bank-details-validation';

type OnboardingBAVRequestBody = {
  traceId: string;
  requestId: string;
  requestTime: string;
  referenceNumber: string | undefined;
  step: string | undefined;
  subStep: string | undefined;
  bankName: string | undefined;
  accountNumber: string | undefined;
  accountType: string;
  idType?: string;
  companyType?: string;
  idNumber?: string;
  compRegNo?: string;
  firstName?: string;
  surname?: string;
  companyName?: string;
  deviceIds: string[];
};

type OnboardingBAVResponse = {
  traceId: string;
  requestId: string;
  requestTime: string;
  referenceNumber: string;
  step: string;
  subStep: string;
  bankName: string;
  accountNumber: string;
  accountType: string;
  idType: string;
  companyType: string;
  idNumber: string;
  compRegNo: string;
  firstName: string;
  surname: string;
  companyName: string;
  deviceIds: string[];
  responseTime: string;
  responseMillis: number;
  accountAccepted: boolean;
  message: string;
  responseMessage: string;
};

type OnlineBAVRequestBody = {
  requestId: string;
  requestTime: string;
  merchantNumber: string | undefined;
  bankName: string;
  branchCode: string;
  bavAccountType: number | undefined;
  accountNo?: string;
  idType: IdType;
  idCompRegNo: string | undefined;
  initials?: string;
  surnameCompName: string | undefined;
};

type OnlineBAVResponse = {
  requestId: string;
  requestTime: string;
  accountNumber: string;
  bavAccountType: number;
  bankName: string;
  branchCode: string;
  idCompRegNo: string;
  idType: IdType;
  initials: string;
  surnameCompName: string;
  responseTime: string;
  responseMillis: number;
  accountAccepted: boolean;
  message: string;

  result: {
    message: string;
  };
  isBusinessError: boolean;
};

type BAVCheckResult = {
  ok: boolean;
  message?: string;
};

enum CDVStatus {
  INVALID = '0',
  VALID = '1',
}

interface CDVCheckResponse {
  ok: boolean;
  status: number;
  code: CDVStatus;
  message: string;
  description?: string;
}

export interface IBankAccountValidationService {
  getIsBankDetailsValid(
    accountNumber: string,
    accountType: AccountType,
    bankName: string,
    branchCode: string,
    accountOwner: AccountOwner,
    isSouthAfricanIdHolder: boolean,
    abortController?: AbortController,
  ): Promise<boolean>;
  doesCDVCheckPass(
    branchCode: string,
    bankAccountNumber: string,
    accountType: AccountType,
    abortController: AbortController,
  ): Promise<boolean>;
  onboardingBankAccountValidation(
    body: OnboardingBAVRequestBody,
    abortController?: AbortController,
  ): Promise<boolean | string>;
  onlineBankAccountValidation(
    body: OnlineBAVRequestBody,
    abortController: AbortController,
  ): Promise<BAVCheckResult>;
}

export class BankAccountValidationService implements IBankAccountValidationService {
  private baseUrl: string;
  private apiBasePath?: string;
  private cache?: ICacheStore;

  constructor(baseUrl: string, apiBasePath?: string, cache?: ICacheStore) {
    this.baseUrl = baseUrl;
    this.apiBasePath = apiBasePath;
    this.cache = cache;
  }

  private VALIDATE_BANK_ACCOUNT_ENDPOINT_PATH = 'cloud/api/vetting-manage/validateBankAccount';
  private BANK_ACCOUNT_VALIDATION_PATH = 'bav';
  private ONLINE_BANK_ACCOUNT_VALIDATION_PATH = 'bankAccounts/onlineValidation';

  async getIsBankDetailsValid(
    accountNumber: string,
    accountType: AccountType,
    bankName: string,
    branchCode: string,
    accountOwner: AccountOwner,
    isSouthAfricanIdHolder: boolean,
    abortController?: AbortController,
  ): Promise<boolean> {
    const params = {
      accountNumber: accountNumber,
      accountType: accountType,
      bankName: bankName,
      branchCode: branchCode,
      idCompRegNo: '',
      idType: '',
      surnameCompName: '',
    };

    switch (accountOwner.kind) {
      case AccountOwnerKind.Company:
        params.idCompRegNo = accountOwner.companyRegistrationNumber;
        params.surnameCompName = accountOwner.companyName;
        params.idType = accountOwner.idType;
        break;

      case AccountOwnerKind.Individual:
        params.idCompRegNo = accountOwner.idNumber;
        params.idType = accountOwner.idType;
        params.surnameCompName = accountOwner.surname;
        break;
    }

    if (!isSouthAfricanIdHolder) {
      params.idType = 'FPP';
    }

    const response = await request(
      `${this.VALIDATE_BANK_ACCOUNT_ENDPOINT_PATH}?${parseObjectToQueryParamString(params)}`,
      {
        fetchOptions: {
          method: 'GET',
          signal: abortController?.signal || null,
        },
      },
      this.baseUrl,
      null,
      false,
      true,
    );

    const responseCode = response?.status;
    const responseBody = response?.result;

    if (responseCode !== 200 && !abortController?.signal?.aborted) {
      throw new Error(
        `BAV revamp: error response from BAV ${this.VALIDATE_BANK_ACCOUNT_ENDPOINT_PATH} GET endpoint: response code: ${responseCode}`,
      );
    }

    return responseBody === ValidationResult.VALID;
  }

  async doesCDVCheckPass(
    branchCode: string,
    bankAccountNumber: string,
    accountType: AccountType,
    abortController: AbortController,
  ): Promise<boolean> {
    const query = `vetting/manage/cdv?branchCode=${branchCode}&bankAccountNumber=${bankAccountNumber}&accountType=${accountType}`;

    const { response, status } = await Api.get<CDVCheckResponse>(query, {
      signal: abortController.signal,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (status !== 200) {
      throw new Error(
        `CDV Revamp: error response from CDV ${query} GET endpoint: response code: ${status}, response: ${JSON.stringify(
          response,
        )}`,
      );
    }

    return response.code === CDVStatus.VALID;
  }

  async onboardingBankAccountValidation(
    body: OnboardingBAVRequestBody,
    abortController?: AbortController,
  ): Promise<boolean | string> {
    const url = encodeURI(`${this.baseUrl}${this.apiBasePath}${this.BANK_ACCOUNT_VALIDATION_PATH}`);

    try {
      const { response, status } = await Api.post<OnboardingBAVResponse>(url, {
        body: JSON.stringify(body),
        signal: abortController?.signal,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (status >= 500 && !abortController?.signal?.aborted) {
        throw new Error(
          `BAV: error response from BAV ${this.apiBasePath}${
            this.BANK_ACCOUNT_VALIDATION_PATH
          } GET endpoint: response: ${JSON.stringify(response)} code: ${status}`,
        );
      }
      return response.accountAccepted ?? (response.message || response.responseMessage);
    } catch (error) {
      console.error(error);

      throw new Error(
        "Uh oh! We're experiencing technical difficulties, but we're working on it. Please try again later.",
      );
    }
  }

  async onlineBankAccountValidation(
    body: OnlineBAVRequestBody,
    abortController?: AbortController,
  ): Promise<BAVCheckResult> {
    const url = encodeURI(
      `${this.baseUrl}${this.apiBasePath}${this.ONLINE_BANK_ACCOUNT_VALIDATION_PATH}`,
    );

    try {
      const { response, status } = await Api.post<OnlineBAVResponse>(url, {
        body: JSON.stringify(body),
        signal: abortController?.signal,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (status >= 500 && !abortController?.signal?.aborted) {
        throw new Error(
          `BAV: error response from Online BAV ${this.apiBasePath}${
            this.ONLINE_BANK_ACCOUNT_VALIDATION_PATH
          } GET endpoint: response: ${JSON.stringify(response)} code: ${status}`,
        );
      }

      return {
        ok: response.accountAccepted,
        message: response?.message || response?.result?.message,
      };
    } catch (error) {
      console.error(error);

      return {
        ok: false,
        message:
          "Uh oh! We're experiencing technical difficulties, but we're working on it. Please try again later.",
      };
    }
  }
}
